import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { auth, getErrorMessage } from '@/lib/platform';
import { isUsableContactPhone, useContactPhone } from '@/lib/contact';

import { ticketsApi } from '@/lib/api';
import { operatorPolicyKeys, queryKeys } from '@/lib/query-keys';
import { useStationDiscovery, useTransitDraft } from '@/lib/use-transit-booking';
import { openRazorpayCheckout, PaymentCancelledError } from '@/lib/razorpay';
import { customerEmail, customerName, razorpayEmail } from '@/lib/customer';
import {
  formatMoney,
  formatValidityDuration,
  friendlyTransitError,
  SERVICE_HOURS_NOTE,
  ticketTypeDescription,
  ticketTypeLabel,
} from '@/lib/format';
import { operatorPolicyApi } from '@/lib/operator-policy-api';
import { maxPassengers, resolveOperator, serviceHoursNote } from '@/lib/operator-terms';
import type { FareConcession, FareOption, Station } from '@/lib/types';
import { StationPicker } from '@/components/station-picker';
import { Badge } from '@/components/shared/badge';
import { ErrorState } from '@/components/shared/error-state';
import { InfoNote } from '@/components/shared/info-note';
import { Screen, AccountButton } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Segmented } from '@/components/ui/segmented';
import { Stepper } from '@/components/ui/stepper';
import { Field } from '@/components/ui/field';
import { SumRow } from '@/components/ui/sum-row';
import { Money } from '@/components/ui/money';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { LoadingLabel, ProgressLabel } from '@/components/ui';

const DEFAULT_CITY_METRO = 'std:080'; // Bangalore — Namma Metro (BMRCL) is the live metro BPP
const DEFAULT_CITY_BUS = 'std:080'; // Bangalore — bus city default for testing (TRV11 2.0.1 Workbench)
const defaultCityFor = (v: VehicleCategory) =>
  v === 'BUS' ? DEFAULT_CITY_BUS : DEFAULT_CITY_METRO;

const MOCK_BUS_STOPS: Station[] = Array.from({ length: 37 }, (_, i) => ({
  id: i + 1,
  code: `MOCK_STATION_${i + 1}`,
  name: `Stop ${i + 1}`,
  lines: [],
  isInterchange: false,
}));

const MOCK_METRO_STOPS: Station[] = Array.from({ length: 15 }, (_, i) => ({
  id: i + 1,
  code: `MOCK_STATION_${i + 1}`,
  name: `Stop ${i + 1}`,
  lines: [],
  isInterchange: false,
}));

type VehicleCategory = 'METRO' | 'BUS';

export const Route = createFileRoute('/_authed/metro/')({
  // The launcher's "Bus" card deep-links here with ?mode=bus; the mode toggle below drives
  // the same param (single source of truth). Any absent/other value resolves to metro. `operator`
  // is the chosen BPP id for a multi-operator city (e.g. Mumbai) — likewise URL-driven so a
  // reload / bookmark / back preserves the selection.
  validateSearch: (
    search: Record<string, unknown>
  ): { mode?: 'bus'; operator?: string; city?: string; flow?: 'direct' } => ({
    mode: search.mode === 'bus' ? 'bus' : undefined,
    operator:
      typeof search.operator === 'string' &&
      search.operator &&
      search.operator !== 'null' &&
      search.operator !== 'undefined'
        ? search.operator
        : undefined,
    city:
      typeof search.city === 'string' &&
      search.city &&
      search.city !== 'null' &&
      search.city !== 'undefined'
        ? search.city
        : undefined,
    flow: search.flow === 'direct' ? 'direct' : undefined,
  }),
  component: BookPage,
});

function BookPage() {
  const navigate = useNavigate();
  const { mode, operator, city, flow } = Route.useSearch();
  const isDirectMode = flow === 'direct';

  // Derived from the URL (?mode=bus & ?city=...) so deep-link, toggle, reload, and back/forward all agree —
  // the toggle and city selector write the params rather than separate local state.
  const vehicleCategory: VehicleCategory = mode === 'bus' ? 'BUS' : 'METRO';
  const isBus = vehicleCategory === 'BUS';
  const cityCode =
    city && city !== 'null' && city !== 'undefined' ? city : defaultCityFor(vehicleCategory);
  const citiesQuery = useQuery({
    queryKey: queryKeys.cities(),
    queryFn: () => ticketsApi.cities(),
    staleTime: Infinity, // curated constant on the backend
  });
  const cities = citiesQuery.data?.cities ?? [];
  const cityName = cities.find((c) => c.code === cityCode)?.name ?? 'Metro';

  // Pickable operators (BPPs) for the city — a METRO concept: the picker disambiguates a city with
  // multiple metro BPPs (Mumbai, std:022 — four distinct metro BPPs). Bus has no operator picker (its
  // city selector already points at a single live bus BPP), so skip the query and broadcast-discover
  // as before — otherwise a metro-operator city selected in bus mode would show metro operators.
  const operatorsQuery = useQuery({
    queryKey: queryKeys.operators(cityCode),
    queryFn: () => ticketsApi.operators(cityCode),
    enabled: !isBus && !isDirectMode,
    staleTime: Infinity, // curated config on the backend
  });
  const operators = operatorsQuery.data?.operators ?? [];
  const needsOperatorPicker = !isBus && !isDirectMode && operators.length > 1;

  // Rider-facing policy for the chosen city. Curated config that changes monthly at most,
  // so it is cached hard; every screen below degrades to generic copy when it is absent.
  const policyQuery = useQuery({
    queryKey: operatorPolicyKeys.city(cityCode),
    queryFn: () => operatorPolicyApi.get(cityCode),
    enabled: !isBus && !isDirectMode, // metro-only: the registry holds no bus operator policy
    staleTime: Infinity,
  });
  // The chosen operator (URL-driven), constrained to one actually offered for this city so a stale
  // deep-link param can't select a non-existent operator.
  const selectedOperatorBppId =
    needsOperatorPicker && operator && operators.some((o) => o.bppId === operator) ? operator : '';
  const selectedOperatorName = operators.find((o) => o.bppId === selectedOperatorBppId)?.name ?? '';
  // Hold metro discovery until the city's operators are KNOWN. Before the query resolves, a
  // multi-operator city looks single-operator (empty list) and would fire a BROADCAST search1 — the
  // exact race directed discovery exists to avoid. On an operators-query error, isLoading clears and
  // we fall through to today's broadcast behavior (fail-open). Bus never waits (no operator concept).
  const operatorsResolved = isBus || isDirectMode || !operatorsQuery.isLoading;
  const operatorChosen =
    operatorsResolved && (!needsOperatorPicker || selectedOperatorBppId !== '');

  const {
    stations,
    bppId: discoveredBppId,
    bppUri: discoveredBppUri,
    discoveryTransactionId,
    isLoading: stationsLoading,
    error: stationsError,
    refetch: refreshDiscovery,
    retryDiscovery: refreshSeller,
  } = useStationDiscovery(
    !isDirectMode && operatorChosen ? cityCode : null,
    vehicleCategory,
    selectedOperatorBppId,
    !isDirectMode
  );

  // The operator this booking will run against: the one the rider picked, else whichever
  // BPP actually served the catalog, else the city default. Undefined until the policy
  // loads (or on an older backend) — every consumer below treats that as "say nothing".
  //
  // METRO only. This route serves bus as well (?mode=bus), the registry holds no bus
  // operator, and a policy is keyed by city — so resolving in bus mode would hand a
  // Bangalore bus booking the METRO operator's hours and cap ("Namma Metro runs 4:30 AM…").
  //
  // Named activeOperator, not `operator`: that identifier is already the URL search param
  // carrying the CHOSEN bpp_id (line 58). Two different things.
  const activeOperator = isBus
    ? undefined
    : resolveOperator(policyQuery.data, cityCode, selectedOperatorBppId || discoveredBppId);

  const [from, setFrom] = useState<Station | null>(null);
  const [to, setTo] = useState<Station | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [txnId, setTxnId] = useState<string | null>(null);
  const [selected, setSelected] = useState<FareOption | null>(null);
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState(auth.currentUser?.email ?? '');
  const [payNotice, setPayNotice] = useState('');

  // Google sign-in supplies no phone; the operator-set booking contact does. See lib/contact.ts.
  const [phone, setPhone] = useContactPhone();
  // A late on_confirm starts the TRV11 status(ref_id) → technical-cancel chain after the normal
  // 30s warning. Keep polling so its terminal refund dialog appears without repeated rider action.
  const { draft, timedOut, refetch, resumePolling } = useTransitDraft(txnId, undefined, true);

  const searchMut = useMutation({
    mutationFn: ticketsApi.search,
    onSuccess: (ack) => setTxnId(ack.transactionId),
  });
  // The draft poll stops once a step's status is `completed`; each subsequent
  // action (select/init/confirm) reuses the same txn, so resume polling on success
  // to pick up the next callback (on_select / on_init / on_confirm).
  const selectMut = useMutation({
    mutationFn: ticketsApi.select,
    onSuccess: () => void resumePolling(),
  });
  const initMut = useMutation({
    mutationFn: ticketsApi.init,
    onSuccess: () => void resumePolling(),
  });
  const confirmMut = useMutation({
    mutationFn: ticketsApi.confirm,
    onSuccess: () => void resumePolling(),
  });

  // Navigate to the ticket once the order is confirmed.
  useEffect(() => {
    if (draft?.screen === 'confirmed' && draft.orderId) {
      // Optional status call after confirm:
      // if (draft.transactionId) {
      //   void ticketsApi.status(draft.transactionId, draft.orderId);
      // }
      void navigate({ to: '/metro/$id', params: { id: draft.orderId } });
    }
  }, [draft, navigate]);

  function reset() {
    setTxnId(null);
    setSelected(null);
    setPayNotice('');
    // Clear any lingering select/init failure so a fresh search/switch doesn't show a stale error.
    selectMut.reset();
    initMut.reset();
  }

  function handleSearch() {
    const fromCode = from?.code || from?.name || (from ? String(from.id) : '');
    const toCode = to?.code || to?.name || (to ? String(to.id) : '');
    if (!fromCode || !toCode) return;
    reset();
    searchMut.mutate({
      cityCode,
      fromStationCode: fromCode,
      toStationCode: toCode,
      quantity,
      vehicleCategory,
      // In direct mode (1-step broadcast), do not route directly to a discovery BPP
      // or reuse a discovery transaction ID.
      bppId: isDirectMode ? undefined : (discoveredBppId || undefined),
      bppUri: isDirectMode ? undefined : (discoveredBppUri || undefined),
      transactionId: isDirectMode ? undefined : (discoveryTransactionId ?? undefined),
    });
  }

  function handleFlowChange(direct: boolean) {
    setFrom(null);
    setTo(null);
    reset();
    void navigate({
      to: '/metro',
      search: {
        mode: isBus ? 'bus' : undefined,
        city: cityCode,
        operator: operator || undefined,
        flow: direct ? 'direct' : undefined,
      },
      replace: true,
    });
  }

  // The pinned seller (BPP) failed or stalled the search. Force a FRESH broadcast discovery (the
  // backend drops the cached BPP and re-fans-out) so the catalog re-picks a healthy seller; clear the
  // chosen stations since the new BPP uses its own station codes.
  function handleSwitchSeller() {
    reset();
    setFrom(null);
    setTo(null);
    void refreshSeller();
  }

  function handleCityChange(next: string) {
    if (next === cityCode) return;
    setFrom(null);
    setTo(null);
    reset();
    // A new city has its own operators — drop any chosen operator so the picker starts fresh.
    void navigate({
      to: '/metro',
      search: {
        mode: isBus ? 'bus' : undefined,
        city: next,
        operator: undefined,
        flow: flow || undefined,
      },
      replace: true,
    });
  }

  // Switch operator (BPP) within a multi-operator city. Its stations use its own codes, so clear the
  // selection; the URL param drives discovery + persists across reload.
  function handleOperatorChange(next: string) {
    if (next === selectedOperatorBppId) return;
    setFrom(null);
    setTo(null);
    reset();
    void navigate({
      to: '/metro',
      search: {
        mode: isBus ? 'bus' : undefined,
        city: cityCode,
        operator: next || undefined,
        flow: flow || undefined,
      },
      replace: true,
    });
  }

  function handleModeChange(next: VehicleCategory) {
    if (next === vehicleCategory) return;
    // Bus stops and metro stations are different catalogs — clear the selection.
    setFrom(null);
    setTo(null);
    reset();
    // Reset to the mode's default city: metro is live in Bangalore (std:080), intra-city bus
    // only in Delhi (std:011) on ONDC — so switching mode re-points discovery at a live BPP.
    void navigate({
      to: '/metro',
      search: {
        mode: next === 'BUS' ? 'bus' : undefined,
        city: defaultCityFor(next),
        operator: undefined,
        flow: next === 'BUS' ? flow || undefined : undefined,
      },
      replace: true,
    });
  }

  function handleBack() {
    if (txnId) {
      reset();
      // Re-fetch the existing city discovery transaction. Do not force a new broadcast when backing
      // out: the cached transaction may still be waiting for the same bus seller's callback.
      void refreshDiscovery();
    } else {
      void navigate({ to: '/' });
    }
  }

  function handleContinue() {
    if (!txnId || !selected) return;
    // Opaque fare: the server owns the fare identity (provider/fulfillment/bpp) — recovered from the
    // stored on_search catalog by itemId, and the BPP from the transaction — so select sends only the
    // fare key + quantity.
    selectMut.mutate({
      transactionId: txnId,
      itemId: selected.itemId,
      quantity,
    });
  }

  function handleGetFinalFare() {
    if (!txnId || !selected) return;
    // Identity (item/provider/fulfillment) is server-owned — recovered from the stored on_select — so
    // init sends only the new input: quantity + billing. The `selected` guard keeps the UX precondition.
    initMut.mutate({
      transactionId: txnId,
      quantity,
      billingName: billingName.trim() || customerName() || 'Passenger',
      billingPhone: phone,
      billingEmail: customerEmail(billingEmail) || undefined,
    });
  }

  async function handlePay() {
    if (!txnId || !selected) return;
    setPayNotice('');
    try {
      const order = await ticketsApi.paymentOrder(txnId);
      const success = await openRazorpayCheckout(order, {
        name: billingName.trim() || customerName() || 'Passenger',
        contact: phone,
        email: razorpayEmail(billingEmail),
      });
      // Identity is server-owned (see init) — confirm sends only quantity + billing + payment proof.
      await confirmMut.mutateAsync({
        transactionId: txnId,
        quantity,
        billingName: billingName.trim() || customerName() || 'Passenger',
        billingPhone: phone,
        billingEmail: customerEmail(billingEmail) || undefined,
        razorpayOrderId: success.razorpay_order_id,
        razorpayPaymentId: success.razorpay_payment_id,
        razorpaySignature: success.razorpay_signature,
      });
      void resumePolling(); // resume polling for on_confirm → ticket
    } catch (err) {
      if (err instanceof PaymentCancelledError) {
        setPayNotice('Payment cancelled — try again.');
      } else {
        setPayNotice(getErrorMessage(err));
      }
    }
  }

  // Recovery nudge: during the active booking flow, the draft poller is the intended recovery path.
  // Keep the check inline to avoid making extra /status calls while we are still waiting on a
  // confirm callback, and let the server-side reconcile sweep handle the late-confirm safety net.
  async function handleRecover() {
    if (!txnId) return;
    void refetch();
  }

  // Do not trigger a /status call during the active booking phase; a timed-out confirm is simply
  // retried via the draft poll and the server-side recovery remains the backstop.
  const recoverFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (timedOut && draft?.step === 'confirm' && txnId && recoverFiredRef.current !== txnId) {
      recoverFiredRef.current = txnId;
      void refetch();
    }
  }, [timedOut, draft?.step, txnId, refetch]);

  const actionPending =
    searchMut.isPending || selectMut.isPending || initMut.isPending || confirmMut.isPending;

  // Both are operator-enforced, and both fail AFTER the money is taken, so they gate the flow rather
  // than being validated server-side: BMRCL NACKs an empty billing name (30000), and KMRL NACKs an
  // empty phone at confirm ("Phone is a required field"). The transit DTOs mark neither required, so
  // an empty value reaches the BPP silently — nothing else stops this.
  const phoneOk = isUsableContactPhone(phone);
  const billingOk = billingName.trim() !== '' && phoneOk;

  // Render by the server-decided `screen` (authoritative) — not by inferring from step × status ×
  // field-presence. `choose_fare` surfaces the fares whether the BPP answered a city search with fares
  // directly or via a station-pair search2 — the fix for the hidden-bus-fares bug.
  const showOptions = draft?.screen === 'choose_fare';
  const showBilling = draft?.screen === 'passenger';
  const showReview = draft?.screen === 'review';
  const draftErrored = draft?.screen === 'error';
  const technicalRefunded = draft?.screen === 'refunded';
  const showRefundDialog = technicalRefunded || (draftErrored && Boolean(draft?.refunded));
  const isConfirming =
    !draftErrored &&
    !showRefundDialog &&
    (confirmMut.isPending ||
      (!!txnId &&
        draft?.step === 'confirm' &&
        draft?.screen !== 'confirmed'));
  const fareLoading =
    !timedOut &&
    (searchMut.isPending ||
      (!!txnId &&
        !showOptions &&
        !showBilling &&
        !showReview &&
        !draftErrored &&
        (!draft || draft.step === 'search')));
  const passengerLoading =
    !timedOut &&
    (selectMut.isPending ||
      (!!txnId &&
        !showBilling &&
        !showReview &&
        !draftErrored &&
        draft?.step === 'select'));
  const initLoading =
    !timedOut &&
    (initMut.isPending ||
      (!!txnId &&
        !showReview &&
        !draftErrored &&
        draft?.step === 'init'));
  // A failed select/init POST (bad catalog, seller NACK) — surfaced inline so the wizard doesn't just
  // stall on the current step until the ~30s poll timeout. Cleared by reset() on search/switch.
  const actionError = selectMut.error ?? initMut.error;

  function returnHomeAfterRefund() {
    void navigate({ to: '/', replace: true });
  }

  const stepIndex = showReview ? 3 : showBilling ? 2 : showOptions ? 1 : 0;

  const screenProps = {
    title: isBus ? 'Bus ticket' : 'Metro ticket',
    accent: (isBus ? 'bus' : 'metro') as 'bus' | 'metro',
    onBack: handleBack,
    right: <AccountButton />,
    steps: { total: 4, current: stepIndex },
  };

  if (stationsError && stations.length === 0) {
    return (
      <Screen {...screenProps}>
        {/* Retry DISCOVERY with a fresh broadcast (not the cities query) so a stuck seller is re-picked. */}
        <ErrorState error={stationsError} onRetry={handleSwitchSeller} />
      </Screen>
    );
  }

  return (
    <Screen {...screenProps}>
      <div className="wb-col wb-gap-16">
        <div className="wb-col wb-gap-6">
          <span className="wb-eyebrow">Book · {cityName}</span>
          <h1 className="wb-h1">{isBus ? 'Buy a bus ticket' : 'Buy a metro ticket'}</h1>
        </div>

        {/* Transit mode: metro vs intra-city bus (same TRV11 facade, different catalog) */}
        {!txnId && (
          <Segmented
            ariaLabel="Transit mode"
            value={vehicleCategory}
            onChange={handleModeChange}
            options={[
              { value: 'METRO', label: 'Metro', icon: 'metro' },
              { value: 'BUS', label: 'Bus', icon: 'bus' },
            ]}
          />
        )}

        {/* Bus-only ONDC flow selector: Metro always uses the discovery flow. */}
        {!txnId && isBus && (
          <div className="wb-row wb-gap-8 wb-center" style={{ marginTop: -4 }}>
            <span className="wb-tiny wb-ink3">Flow:</span>
            <button
              type="button"
              className="wb-btn-subtle"
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 16,
                border: '1px solid var(--border)',
                background: !isDirectMode ? 'var(--accent-subtle, #e0f2fe)' : 'transparent',
                fontWeight: !isDirectMode ? 600 : 400,
                cursor: 'pointer',
              }}
              onClick={() => handleFlowChange(false)}
            >
              2-Step Discovery
            </button>
            <button
              type="button"
              className="wb-btn-subtle"
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 16,
                border: '1px solid var(--border)',
                background: isDirectMode ? 'var(--accent-subtle, #e0f2fe)' : 'transparent',
                fontWeight: isDirectMode ? 600 : 400,
                cursor: 'pointer',
              }}
              onClick={() => handleFlowChange(true)}
            >
              1-Step Direct Search
            </button>
          </div>
        )}

        {/* City selector */}
        {!txnId && (
          <div>
            <label className="wb-label" htmlFor="city">
              City
            </label>
            <select
              id="city"
              value={cityCode}
              onChange={(e) => handleCityChange(e.target.value)}
              disabled={citiesQuery.isLoading}
              className="wb-field"
            >
              {cities.length === 0 && <option value={cityCode}>Loading…</option>}
              {cities.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Operator selector — shown only when a city has more than one metro operator (e.g. Mumbai).
            The chosen operator routes discovery directly to that BPP. */}
        {!txnId && needsOperatorPicker && (
          <div>
            <label className="wb-label" htmlFor="operator">
              Operator
            </label>
            <select
              id="operator"
              value={selectedOperatorBppId}
              onChange={(e) => handleOperatorChange(e.target.value)}
              className="wb-field"
            >
              <option value="">Choose an operator…</option>
              {operators.map((o) => (
                <option key={o.bppId} value={o.bppId}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Prompt to pick an operator before stations can load (multi-operator city, none chosen). */}
        {!txnId && needsOperatorPicker && !operatorChosen && (
          <p className="wb-sub" style={{ padding: '18px 0', textAlign: 'center' }}>
            Choose an operator to see {cityName} stations.
          </p>
        )}

        {/* Station selection */}
        {!txnId && operatorChosen && (
          <div className="wb-col wb-gap-12">
            {stationsLoading && stations.length === 0 && (
              <p className="wb-tiny wb-ink3" style={{ margin: 0, textAlign: 'center' }}>
                Discovering live stops for {cityName}…
              </p>
            )}
            <StationPicker
              label="From"
              stations={stations.length > 0 ? stations : isBus ? MOCK_BUS_STOPS : MOCK_METRO_STOPS}
              selected={from}
              exclude={to}
              onSelect={setFrom}
              scope={`${cityCode}:${selectedOperatorBppId}`}
            />
            <div className="wb-row wb-center">
              <IconButton
                name="swap"
                label="Swap stations"
                onClick={() => {
                  setFrom(to);
                  setTo(from);
                }}
              />
            </div>
            <StationPicker
              label="To"
              stations={stations.length > 0 ? stations : isBus ? MOCK_BUS_STOPS : MOCK_METRO_STOPS}
              selected={to}
              exclude={from}
              onSelect={setTo}
              scope={`${cityCode}:${selectedOperatorBppId}`}
            />
            <Button
              variant="primary"
              block
              disabled={!from || !to || searchMut.isPending}
              onClick={handleSearch}
            >
              {searchMut.isPending ? 'Searching…' : 'Find tickets'}
            </Button>
            {searchMut.error && (
              <p className="wb-tiny" style={{ color: 'var(--critical-600, #dc2626)', margin: 0, textAlign: 'center' }}>
                {searchMut.error instanceof Error ? searchMut.error.message : 'Failed to search routes'}
              </p>
            )}
          </div>
        )}

        {/* Operating-hours context — a late-night search legitimately finds nothing. Uses
            THIS operator's published hours when we have them; the generic note otherwise,
            never another operator's times. */}
        {!txnId && stations.length > 0 && (
          <InfoNote>{serviceHoursNote(activeOperator) || SERVICE_HOURS_NOTE}</InfoNote>
        )}

        {/* Polling / waiting indicator */}
        {fareLoading && <LoadingLabel label="Loading fare details…" />}

        {passengerLoading && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12" style={{ background: 'var(--surface-sunken)' }}>
            <div className="wb-row wb-gap-8 wb-center">
              <span className="wb-spinner" />
              <p className="wb-strong" style={{ margin: 0 }}>
                Selecting ticket & fetching details…
              </p>
            </div>
            <div className="wb-col wb-gap-6 wb-small" style={{ opacity: 0.9 }}>
              <SumRow label="Route">
                {from && to
                  ? `${from.name} → ${to.name}`
                  : selected?.journey?.from?.name
                    ? `${selected.journey.from.name} → ${selected.journey.to.name}`
                    : (selected?.ticketName ?? '')}
              </SumRow>
              <SumRow label="Ticket">{selected.ticketName}</SumRow>
              <SumRow label="Quantity">{quantity} {quantity === 1 ? 'ticket' : 'tickets'}</SumRow>
            </div>
          </div>
        )}

        {initLoading && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12" style={{ background: 'var(--surface-sunken)' }}>
            <div className="wb-row wb-gap-8 wb-center">
              <span className="wb-spinner" />
              <p className="wb-strong" style={{ margin: 0 }}>
                Initializing booking with operator (ONDC /init)…
              </p>
            </div>
            <div className="wb-col wb-gap-6 wb-small" style={{ opacity: 0.9 }}>
              <SumRow label="Route">
                {from && to
                  ? `${from.name} → ${to.name}`
                  : selected?.journey?.from?.name
                    ? `${selected.journey.from.name} → ${selected.journey.to.name}`
                    : (selected?.ticketName ?? '')}
              </SumRow>
              <SumRow label="Ticket">{selected.ticketName}</SumRow>
              <SumRow label="Quantity">{quantity} {quantity === 1 ? 'ticket' : 'tickets'}</SumRow>
              {billingName.trim() && (
                <SumRow label="Passenger">{billingName.trim()} ({phone})</SumRow>
              )}
              {draft?.quote && (
                <SumRow label="Total Fare">
                  <Money v={formatMoney(draft.quote.total).replace('₹', '')} strong />
                </SumRow>
              )}
            </div>
          </div>
        )}

        {/* Timeout */}
        {timedOut && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{ color: 'var(--honey-700)', background: 'var(--honey-wash-2)' }}
          >
            {draft?.step === 'confirm'
              ? 'Payment received — finalizing your ticket. This can take a moment.'
              : 'Taking longer than usual — the operator may be busy or outside service hours.'}{' '}
            <button
              type="button"
              onClick={() => void (draft?.step === 'confirm' ? handleRecover() : resumePolling())}
              className="wb-strong"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {draft?.step === 'confirm' ? 'Check now' : 'Retry'}
            </button>
            {draft?.step !== 'confirm' && (
              <>
                {' · '}
                <button
                  type="button"
                  onClick={handleSwitchSeller}
                  className="wb-strong"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'inherit',
                  }}
                >
                  Try another operator
                </button>
              </>
            )}
          </div>
        )}

        {/* Per-step error. `refunded` is set whenever the booking failed after the paid confirm stage
            (a BPP confirm error such as 91206, or an exhausted reconciliation/RECON_EXH); the captured
            payment is auto-refunded server-side, so reassure the user rather than show a bare error. */}
        {draftErrored && draft?.error && !draft.refunded && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-8 wb-small"
            style={{
              color: 'var(--danger)',
              background: 'color-mix(in oklab, var(--danger) 10%, var(--surface))',
              borderColor: 'color-mix(in oklab, var(--danger) 30%, transparent)',
            }}
          >
            <p>
              {draft.refunded
                ? 'We couldn’t complete your booking with the metro operator, so your payment has been refunded. Any amount debited will be reversed to your account within 5–7 business days.'
                : friendlyTransitError(draft.error)}
            </p>
            {draft.refunded ? (
              <div>
                <Button variant="secondary" size="sm" onClick={reset}>
                  Start over
                </Button>
              </div>
            ) : (
              <>
                <p style={{ opacity: 0.85 }}>
                  This can happen outside the operator’s service hours, or if the operator doesn’t
                  serve this route. Try another operator, or search again during service hours.
                </p>
                <div>
                  <Button variant="secondary" size="sm" onClick={handleSwitchSeller}>
                    Try another operator
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Select / init failure — actionable instead of a silent stall. */}
        {actionError && !draftErrored && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-8 wb-small"
            style={{
              color: 'var(--danger)',
              background: 'color-mix(in oklab, var(--danger) 10%, var(--surface))',
              borderColor: 'color-mix(in oklab, var(--danger) 30%, transparent)',
            }}
          >
            <p>{getErrorMessage(actionError)}</p>
            <p style={{ opacity: 0.85 }}>
              This can happen if the operator’s catalog is incomplete or the route isn’t served
              right now. Start over, or try another operator.
            </p>
            <div className="wb-row wb-gap-8">
              <Button variant="secondary" size="sm" onClick={reset}>
                Start over
              </Button>
              <Button variant="secondary" size="sm" onClick={handleSwitchSeller}>
                Try another operator
              </Button>
            </div>
          </div>
        )}

        {/* Fare options */}
        {showOptions && (
          <div className="wb-col wb-gap-12">
            <p className="wb-sub">
              {from && to ? `${from.name} → ${to.name}` : 'Available tickets'}
              {needsOperatorPicker && selectedOperatorName ? ` · ${selectedOperatorName}` : ''}
            </p>
            {(draft?.options ?? []).map((opt) => (
              <FareOptionCard
                key={opt.itemId}
                option={opt}
                selected={selected?.itemId === opt.itemId}
                onSelect={() => {
                  setSelected(opt);
                  setQuantity(Math.max(1, opt.minQuantity));
                }}
              />
            ))}
            {(draft?.options ?? []).length === 0 && (
              <div className="wb-card wb-card--pad wb-col wb-gap-8">
                <p className="wb-strong">No tickets available for this route right now.</p>
                <p className="wb-small">
                  This usually means the route isn’t served, or it’s outside the operator’s service
                  hours. You can try a different station pair, or switch to another operator.
                </p>
                <div>
                  <Button variant="secondary" size="sm" onClick={handleSwitchSeller}>
                    Try another operator
                  </Button>
                </div>
              </div>
            )}
            {selected && (
              <div className="wb-card wb-card--pad wb-row wb-between">
                <Stepper
                  value={quantity}
                  min={Math.max(1, selected.minQuantity)}
                  // Operator cap first, then whatever this fare's catalog advertised, then
                  // the historical 6 — the same order the backend resolves it in, so the
                  // stepper can't offer a quantity that select/init would reject.
                  max={Math.max(
                    Math.max(1, selected.minQuantity),
                    maxPassengers(activeOperator, selected.maxQuantity)
                  )}
                  onChange={setQuantity}
                />
                <Button variant="primary" onClick={handleContinue} disabled={actionPending}>
                  Continue to pay
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Billing (after select) */}
        {showBilling && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-14">
            <h2 className="wb-h2">Passenger details</h2>

            {/* Journey Details preview */}
            <div
              className="wb-card wb-card--pad wb-col wb-gap-6 wb-small"
              style={{ background: 'var(--surface-sunken)', border: '1px solid var(--border)' }}
            >
              <SumRow label="Route">
                {from && to
                  ? `${from.name} → ${to.name}`
                  : selected?.journey?.from?.name
                    ? `${selected.journey.from.name} → ${selected.journey.to.name}`
                    : (selected?.ticketName ?? '')}
              </SumRow>
              <SumRow label="Ticket">{selected.ticketName}</SumRow>
              <SumRow label="Quantity">{quantity} {quantity === 1 ? 'ticket' : 'tickets'}</SumRow>
              {draft?.quote && (
                <SumRow label="Fare">
                  <Money v={formatMoney(draft.quote.total).replace('₹', '')} strong />
                </SumRow>
              )}
            </div>
            <Field
              label="Name"
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              placeholder="Full name"
            />
            <Field
              label="Email (optional — for your payment receipt)"
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              placeholder="you@example.com"
            />
            {/* Contact phone is EDITABLE HERE, not just on /account, and blocks the flow when it is
                missing. It used to render as static text — so with nothing in localStorage it read
                "Tickets sent to " and still let you through to payment. BMRCL tolerates an empty
                billing phone at confirm; KMRL does not, and NACKs with "Phone is a required field"
                AFTER the payment is captured. The rider then gets an auto-refund and a generic
                failure message, having paid for nothing. A required field costs one keystroke and
                removes the whole failure mode. */}
            <Field
              label="Phone (for the ticket and any cancellation)"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="90000 00000"
            />
            {!phoneOk && phone.trim() !== '' && (
              <p className="wb-tiny" style={{ color: 'var(--danger)' }}>
                Enter a 10-digit mobile number.
              </p>
            )}
            <Button
              variant="primary"
              block
              onClick={handleGetFinalFare}
              disabled={actionPending || !billingOk}
            >
              Continue
            </Button>
            {!billingOk && (
              <p className="wb-tiny">
                The operator requires a name and phone on every booking — without them the ticket is
                rejected after payment.
              </p>
            )}
          </div>
        )}

        {/* Confirming ticket / waiting on on_confirm loader */}
        {isConfirming && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <ProgressLabel
              label="Confirming your ticket…"
              sub="Payment verified. Connecting to the transport operator to generate your QR ticket. Please stay on this page."
              timedOut={timedOut}
              showRetry={timedOut}
              onRetry={handleRecover}
            />
          </div>
        )}

        {/* Review + Pay (after init) */}
        {showReview && selected && !isConfirming && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <h2 className="wb-h2">Review</h2>
            <div>
              <SumRow label="Route">
                {from && to
                  ? `${from.name} → ${to.name}`
                  : selected?.journey?.from?.name
                    ? `${selected.journey.from.name} → ${selected.journey.to.name}`
                    : (selected?.ticketName ?? '')}
              </SumRow>
              <SumRow label="Ticket">{selected.ticketName}</SumRow>
              <SumRow label="Quantity">{quantity}</SumRow>
              {(draft?.quote?.breakup ?? []).length > 1 &&
                draft?.quote?.breakup?.map((b, i) => (
                  <SumRow key={`${b.title}-${i}`} label={humanize(b.title)}>
                    {formatMoney(b.amount)}
                  </SumRow>
                ))}
              <hr className="wb-divider" style={{ margin: '6px 0' }} />
              <SumRow label="Total" big>
                {formatMoney(draft?.quote?.total ?? selected.fare)}
              </SumRow>
            </div>
            {ticketTypeDescription(selected.ticketType) && (
              <p className="wb-tiny">
                <span className="wb-strong" style={{ color: 'var(--ink)' }}>
                  {ticketTypeLabel(selected.ticketType)}
                </span>{' '}
                — {ticketTypeDescription(selected.ticketType)}
              </p>
            )}
            <p className="wb-tiny">Price includes all taxes and fees.</p>
            {payNotice && (
              <p className="wb-small" style={{ color: 'var(--danger)' }}>
                {payNotice}
              </p>
            )}
            <Button variant="primary" block onClick={handlePay} disabled={confirmMut.isPending}>
              {confirmMut.isPending ? 'Confirming…' : 'Pay'}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showRefundDialog}
        onClose={returnHomeAfterRefund}
        title="Booking could not be completed"
        confirmLabel="Return home"
      >
        <div className="wb-col wb-gap-12">
          <InfoNote>
            {technicalRefunded
              ? 'The operator did not confirm your ticket within the allowed time, so the booking was cancelled automatically.'
              : 'We could not complete your booking with the operator.'}
          </InfoNote>
          <p className="wb-small">
            If any amount was debited, it will be refunded to your original payment method within
            5–7 working days.
          </p>
        </div>
      </ConfirmDialog>

    </Screen>
  );
}

function FareOptionCard({
  option,
  selected,
  onSelect,
}: {
  option: FareOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-checked={selected}
      className={`wb-option ${selected ? 'is-selected' : ''}`}
    >
      <span className="wb-grow">
        <span className="wb-strong" style={{ display: 'block' }}>
          {option.ticketName}
        </span>
        {ticketTypeDescription(option.ticketType) && (
          <span className="wb-tiny" style={{ display: 'block' }}>
            {ticketTypeDescription(option.ticketType)}
          </span>
        )}
        {formatValidityDuration(option.validityDuration) && (
          <span className="wb-tiny" style={{ display: 'block' }}>
            Valid {formatValidityDuration(option.validityDuration)}
          </span>
        )}
        {option.journey.interchanges && option.journey.interchanges.length > 0 && (
          <span className="wb-tiny" style={{ display: 'block' }}>
            Change at {option.journey.interchanges.map((i) => i.stationName).join(', ')}
          </span>
        )}
        {(option.journey.variant || (option.concessions?.length ?? 0) > 0) && (
          <span className="wb-row wb-wrap wb-gap-6" style={{ marginTop: 6 }}>
            {option.journey.variant && (
              <Badge variant={option.journey.variant === 'AC' ? 'blue' : 'default'}>
                {formatVariant(option.journey.variant)}
              </Badge>
            )}
            {option.concessions?.map((c, i) => (
              <Badge key={i} variant="purple">
                🎓 {humanize(c.restrictedPerson)}
              </Badge>
            ))}
          </span>
        )}
        {concessionProofs(option.concessions).length > 0 && (
          <span className="wb-tiny" style={{ display: 'block', marginTop: 2 }}>
            Needs: {concessionProofs(option.concessions).join(', ')}
          </span>
        )}
      </span>
      <Money v={formatMoney(option.fare).replace('₹', '')} strong />
    </button>
  );
}

// --- bus catalog formatting (TRV11 2.0.1) ---

function formatVariant(v: string): string {
  return v === 'NON_AC' ? 'Non-AC' : v;
}

// humanize turns an ONDC enum token (STUDENT, SENIOR_CITIZEN, STUDENT_ID) into a
// readable label (Student, Senior Citizen, Student Id).
function humanize(s: string | undefined): string {
  if (!s) return '';
  return s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function concessionProofs(concessions: FareConcession[] | undefined): string[] {
  return (concessions ?? []).map((c) => humanize(c.restrictionProof)).filter((p) => p.length > 0);
}
